import { forwardRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { colors } from "brand";
import { RideStatusBadge, TermReasonChip } from "common-fe";
import { useTranslation } from "i18n-fe";
import {
  RIDE_STATUSES,
  dropFvdPlaceholderTestName,
  isScoreSuppressed,
  sortJudgesForDisplay,
  SUPPRESSED_SCORE_DISPLAY,
} from "score-common";
import { RIDE_ROLE, RESULT_STATUS } from "../utils/orderOfGo";
import { formatTime } from "../utils/formatTime";
import { formatOrdinal } from "../utils/formatOrdinal";
import { formatComposite } from "../utils/formatComposite";
import { formatElapsedSeconds } from "../utils/presence";
import { termChipReason, termChipLabels } from "../utils/termChip";
import SectionTag from "./SectionTag";
import { formatClassNumber } from "../utils/formatClassNumber";

// #642 — ONE ride in the running order.
// #643 — expands (accordion, collapsed by default) to the full announcer
// script: class/test/qualifying, horse detail, pedigree, owner/breeder, and
// the rider's home town — the SAME fields, in the SAME order, as the printed
// `announcer-order-of-go` report's per-ride block (docs/score/announcer.md),
// so the announcer's eye already knows where to look. Every field in that
// script comes off `row` (joined in ../utils/orderOfGo.js buildRideRow) — no
// fetch here, and all of it is #525 ingest-carry, so ALL of it is optional:
// a horse with no sire is normal, never an error, and a missing field never
// renders as a bare label over nothing.
//
// Read at arm's length, out loud, under time pressure: big type, high contrast,
// minimal chrome (docs/score/announcer.md, "Screens").
//
// ⚠️ The brand theme's `primary.main` is dark grey and vanishes on a dark
// background — every emphasis colour here is `colors.foxOrange` or `text.*`.

const ROLE_STYLES = {
  [RIDE_ROLE.CURRENT]: {
    testId: "role-current",
    sx: { bgcolor: colors.foxOrange, color: "#000", fontWeight: 800 },
  },
  [RIDE_ROLE.ON_DECK]: {
    testId: "role-on-deck",
    sx: {
      bgcolor: "transparent",
      color: colors.foxOrange,
      border: `2px solid ${colors.foxOrange}`,
      fontWeight: 700,
    },
  },
  // #2131 (docs/score/ring-truth.md rule 7) — RIDE_ROLE.NEXT (a second,
  // "next after next" chip) is retired: ring-truth defines exactly ONE
  // next-ride concept (ON_DECK above), so a second tier had no source of
  // truth to read from and was itself an independent derivation.
};

// Static literal keys only — never t(`oog.${role}`).
//
// #2153 -- "In the ring" is an occupancy claim read aloud to the crowd. A ride
// the C judge has only belled (ENTERING_RING) is still walking in, so its
// CURRENT chip says "Entering ring" instead; the chip is the ONE statement of
// status on that row (the RideStatusBadge below is dropped for it, see the
// dedupe there), so the row can never say both phrases at once.
function roleLabel(role, status, t) {
  if (role === RIDE_ROLE.CURRENT) {
    return status === RIDE_STATUSES.ENTERING_RING ? t("oog.enteringRing") : t("oog.inTheRing");
  }
  if (role === RIDE_ROLE.ON_DECK) return t("oog.onDeck");
  return "";
}

// Judges are labelled by their single-letter ring position (C/E/H/M/B, from the
// class judgeAssignments — same convention as scribe-fe's JudgePositionBadge),
// falling back to the opaque judgeId only when score-api couldn't resolve one.
function judgeDisplay(entry) {
  return entry.position || entry.judgeId || "?";
}

// #3369 -- React key for a perJudge row. judgeDisplay() is a LABEL and collides
// ("?") for any two judges with no resolved position and no id, so React
// drops/duplicates one. Key by the judge's unique id, then position, then the
// row's index (perJudge order is stable within a render).
function judgeKey(entry, index) {
  return entry.judgeId || entry.position || `idx-${index}`;
}

// One label -> value line. Renders nothing when the value is empty, so a
// missing #525 field never leaves a bare label behind (AC#2).
function DetailLine({ label, value, testId }) {
  if (!value) return null;
  return (
    <Box data-testid={testId}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", lineHeight: 1.6, letterSpacing: 1 }}
      >
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={500} sx={{ color: "text.primary" }}>
        {value}
      </Typography>
    </Box>
  );
}

const RideRow = forwardRef(function RideRow({ row, result, presence }, ref) {
  const { t } = useTranslation("announcer");
  const [expanded, setExpanded] = useState(false);
  const isScratched = row.status === RIDE_STATUSES.SCRATCHED;
  const isCurrent = row.role === RIDE_ROLE.CURRENT;
  const roleStyle = row.role ? ROLE_STYLES[row.role] : null;

  // #644/#699 — this ride's result state drives the right-hand badge. score-api
  // (#697) reports PARTIAL | CONFIRMED | FINAL and WITHHOLDS every number while
  // PARTIAL (composite null, no per-judge %), so one judge's mark can never
  // render as if it were the score. Reveal is driven ONLY by result.status
  // (never row.status/cache), so an offline CONFIRMED ride is never mistaken for
  // a signed FINAL one (#699):
  //   PARTIAL   → per-judge "committed" chips, NO scores, NO composite.
  //   CONFIRMED → composite + each judge's %, green "ready to announce" (visually
  //               distinct from FINAL's orange placing badge).
  //   FINAL     → unchanged: the orange placing/composite badge.
  const resultStatus = result?.status ?? null;
  // Card #2840 — a ride carrying a term reason NEVER reports a score value,
  // regardless of what result.composite/result.placing/perJudge[].percentage
  // actually hold. This is NOT redundant with score-api's own suppression
  // (results.js, AC5): score-common's calculateRideComposite still returns a
  // real 0 (not null) for a sheet-level elimination by longstanding design
  // (the #2053 "any term reason zeroes the ride score" product decision, a
  // math fact used for sorting/aggregation) — it is not withheld the way a
  // PARTIAL ride's numbers are. Every DISPLAY surface is responsible for
  // re-gating on its own resolved term reason before rendering, the same
  // pattern scoreadmin-fe's ClassPlacings.jsx uses (suppressScoreValue keyed
  // off termReasonsById). row.termReason here is resolved by orderOfGo.js's
  // buildRideRow via score-common's single resolver, deriveRideTermReason
  // (ride, sheets) — the one test this card allows, no second one invented
  // here.
  const isTermSuppressed = isScoreSuppressed(row.termReason);
  const isResultFinal = resultStatus === RESULT_STATUS.FINAL;
  const isResultConfirmed = resultStatus === RESULT_STATUS.CONFIRMED && !isTermSuppressed;
  const isResultPartial = resultStatus === RESULT_STATUS.PARTIAL;
  // #2788: score-api hands perJudge back in whatever order the sheets
  // resolved, so sort to the arena order (E, H, C, M, B, #2786) here rather
  // than trust it. Both per-judge render sites below read this one array.
  const perJudge = sortJudgesForDisplay(result?.perJudge ?? [], (j) => j.position);
  const expectedJudgeCount = result?.expectedJudgeCount ?? 0;
  const confirmedCount = result?.confirmedCount ?? perJudge.length;
  // #2178 — throwaway stopgap (bugs #143/#144, card 2145 deletes this whole
  // file): a FINAL ride's placing is either shown ("1st — 68.500%", plain
  // text, dominant) or the ride is unplaced. Bug #143 — an unplaced ride is
  // STORED data (result.placing === 0/null), not a rendering gap, and must
  // never shout: no fill, no warning color, just "UNPLACED" in quiet secondary
  // text (isResultFinalUnplaced below). Nothing here adds 1 to result.placing.
  // #2322 REVISES that: the quiet thing is the PLACING, not the SCORE. An
  // unplaced FINAL ride still renders its composite, large and orange, above
  // the quiet UNPLACED label — see the render block below. Dropping the number
  // with the placing left the announcer reading "UNPLACED" on rides whose
  // score ClassResults was printing on the same screen.
  // #2183 AC5 — a DSHB place-only ride (buildRideResult, score-api/results.js)
  // carries composite: null, genuinely (no score box on that sheet at all —
  // DR211.1/DR210.5). formatComposite(null) is "" (see its own comment), so
  // interpolating it into "{{place}} — {{composite}}%" would still read as
  // "1st — %", a leftover artifact of a score that was never taken. Drop the
  // whole composite clause for that case instead — DR211.5 requires the
  // PLACING to publish, not an invented percentage. (The unplaced case never
  // shows a composite at all, so it needs no such guard — see
  // isResultFinalUnplaced/results.unplacedLabel.)
  const hasComposite = !isTermSuppressed && Number.isFinite(result?.composite);
  const placingLabel =
    !isTermSuppressed && isResultFinal && result.placing
      ? hasComposite
        ? t("results.placingBadge", {
            place: formatOrdinal(result.placing),
            composite: formatComposite(result.composite),
          })
        : t("results.placingBadgeNoScore", { place: formatOrdinal(result.placing) })
      : "";
  const isResultFinalUnplaced = !isTermSuppressed && isResultFinal && !result.placing;

  // A scratch is a status, not a deletion — the row STAYS (no expand, no hover)
  // and carries a red Scratched chip, so the announcer knows not to expect that
  // horse. Same treatment as scribe-fe's RideList (#460, #3399).
  // #2128 -- an eliminated ride (incl. one terminated ONLY via a sheet-level
  // eliminations[] entry, resolved into row.termReason by orderOfGo.js) is
  // flagged in the COLLAPSED summary, not just in the expanded detail chip.
  // #3399 -- a terminated ride shows ONE red term-reason chip (never a
  // strikethrough, rider/horse stay normal ink). Scratched fallback included.
  const chipReason = termChipReason(row);

  // Same field order as announcer-order-of-go.template.js's per-ride block:
  // class/test/qualifying, horse detail, pedigree, owner+breeder, address.
  // #1837 — testName can be FVD's "FVD Test <id>" breadcrumb (a missing
  // test-map entry); that's a staff-only signal, never announcer/public copy.
  const testName = dropFvdPlaceholderTestName(row.testName);
  const classParts = [formatClassNumber(row.classNumber), row.className].filter(Boolean).join(" ");
  const classLine = [classParts, testName].filter(Boolean).join(" · ");
  const hasClassDetail = Boolean(classLine || row.qualifying || row.termReason);
  const hasAnyDetail =
    hasClassDetail || row.horseDetail || row.pedigree || row.ownerLine || row.address;

  return (
    <Accordion
      ref={ref}
      expanded={expanded}
      onChange={(_event, next) => setExpanded(next)}
      disableGutters
      data-testid="ride-row"
      data-ride-id={row.id}
      data-role={row.role || undefined}
      data-scratched={isScratched || undefined}
      aria-disabled={isScratched || undefined}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "solid",
        // The current ride is PINNED visually — it must be findable at a glance
        // from across a ring stand, in either theme.
        borderWidth: isCurrent ? 3 : 1,
        borderColor: isCurrent ? colors.foxOrange : "divider",
        boxShadow: isCurrent ? 6 : 0,
        // A scratched ride carries no action — no expand, no hover affordance.
        pointerEvents: isScratched ? "none" : "auto",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon data-testid="ride-row-expand-icon" sx={{ color: "text.secondary" }} />
        }
        aria-label={expanded ? t("detail.collapse") : t("detail.expand")}
        data-testid="ride-row-summary"
        sx={{ px: 2, py: 1 }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ flex: 1 }}
        >
          <Box sx={{ minWidth: 96 }}>
            <Typography variant="h5" fontWeight={700}>
              {formatTime(row.scheduledTime, row.scheduledTimeZone)}
            </Typography>
          </Box>

          {/* #1896/#2066 — this Chip has no onClick, so it is not the
              AccordionSummary's toggle control, but a plain click on it
              still bubbles up through the DOM to that summary's own click
              handler (nothing here stops it) and toggles the row open/
              closed as an unintended side effect of tapping what looks
              like a static badge. The wrapper below stops that
              propagation. It renders as `display: inline-flex`, NOT
              `display: contents` — this Stack's spacing is MUI's default
              margin-based `& > :not(style) ~ :not(style)` sibling
              selector (useFlexGap is off, unset anywhere in brand/
              announcer-fe), which only matches a real rendered sibling
              box. A `display: contents` wrapper generates no box, so it
              would not match that selector and the 16px gap before this
              chip would be silently dropped, shifting it 16px left and
              closing the gap to the ride time. `inline-flex` keeps the
              wrapper a normal box in the row so the Stack's margin still
              lands on it exactly as it did on the bare Chip, preserving
              the original geometry (chip left 112px, unchanged). The
              Chip itself stays exactly the plain, unmodified
              <Chip variant="outlined" /> (still no onClick prop) the
              #1896 regression test needs, unchanged. */}
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{ display: "inline-flex" }}
          >
            <Chip
              label={row.competitorNumber || "—"}
              data-testid="ride-cno"
              sx={{
                fontSize: "1.15rem",
                fontWeight: 800,
                height: 40,
                minWidth: 64,
                color: "text.primary",
                borderColor: "divider",
              }}
              variant="outlined"
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h5" fontWeight={700}>
              {row.riderName || "—"}
              <SectionTag section={row.section} />
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {row.horseName || "—"}
            </Typography>
          </Box>

          <Stack spacing={0.5} alignItems="flex-end">
            {/* #2178 (bugs #143/#144) — throwaway stopgap, deleted whole-cloth
                by card 2145. Evin: "ditch the chiclet view of the score...
                shown in a much clearer text view." The FINAL placing/
                composite is no longer a Chip — plain, large, bold text,
                sized past the rider-name h5 above so the score reads as the
                dominant thing on the row, legible at arm's length on a small
                screen. Unplaced (bug #143) is the quiet opposite: no fill,
                no warning color, no composite tacked on, just "UNPLACED" in
                ordinary secondary text — it must never shout. */}
            {/* Card #2840 — a term'd ride (isTermSuppressed) never shows a
                score, percentage, subtotal or placing here: one em dash,
                muted (never the orange "real placement" color), regardless
                of resultStatus (FINAL/CONFIRMED all short-circuit above via
                isTermSuppressed, so this is the only badge that CAN render
                for one of these rides). */}
            {isTermSuppressed ? (
              <Typography
                variant="h4"
                fontWeight={800}
                data-testid="ride-score-suppressed"
                color="text.secondary"
                sx={{ lineHeight: 1.15 }}
              >
                {SUPPRESSED_SCORE_DISPLAY}
              </Typography>
            ) : null}
            {placingLabel ? (
              <Typography
                variant="h4"
                fontWeight={800}
                data-testid="ride-placing-badge"
                data-placing={result.placing ?? undefined}
                sx={{ color: colors.foxOrange, lineHeight: 1.15 }}
              >
                {placingLabel}
              </Typography>
            ) : null}
            {/* #2322 — a FINAL ride's SCORE is not the same fact as its
                PLACING, and hiding the first behind the second took the number
                off the announcer's screen for every class that had not been
                placed yet: the row read a bare "UNPLACED" while ClassResults,
                two inches above it on the same screen, printed 67.188% for the
                very same ride. Bug #143's "an unplaced ride must never shout"
                still holds, but it is the PLACING that stays quiet, not the
                score. Render the composite in the same large bold orange text a
                placed ride gets, with UNPLACED underneath it in ordinary
                secondary text. The #2183 AC5 / #2218 no-fabricated-percentage
                guard survives as the hasComposite test: a DSHB place-only ride
                genuinely carries composite: null and still renders UNPLACED
                alone, never an empty "%". */}
            {isResultFinalUnplaced && hasComposite ? (
              <Typography
                variant="h4"
                fontWeight={800}
                data-testid="ride-unplaced-score"
                data-composite={result.composite}
                sx={{ color: colors.foxOrange, lineHeight: 1.15 }}
              >
                {t("results.unplacedScore", { composite: formatComposite(result.composite) })}
              </Typography>
            ) : null}
            {isResultFinalUnplaced ? (
              <Typography
                variant="h6"
                fontWeight={700}
                color="text.secondary"
                data-testid="ride-placing-badge"
                data-placing={result.placing ?? undefined}
               
              >
                {t("results.unplacedLabel")}
              </Typography>
            ) : null}
            {/* #699 CONFIRMED — every judge confirmed, not yet signed. Reveal the
                composite + each judge's %, in green ("ready to announce"), which
                MUST read differently from FINAL's orange placing text above. The
                success colour matches common-fe RideStatusBadge's CONFIRMED/FINAL
                chip, and is legible in both themes (MUI semantic success).
                #2178 — same text-first swap as the FINAL badge above: plain text,
                not a Chip, sized to stay dominant over rider metadata. Each
                judge's score stacks on its OWN line, reading "Judge at
                <letter>: XX.XXX%" large enough for across a ring stand.
                #2183 AC5 / #2218 — same no-fabricated-percentage guards as
                placingLabel above: a place-only ride can reach CONFIRMED (marks
                confirmed) before FINAL (signed) with composite: null, and a
                DSHB place-only sheet (score-api buildRideResult
                isDshbPlacementOnly) omits percentage entirely on perJudge. Fall
                back to the NoScore sibling so the "which judges are in" signal
                survives without an invented number. */}
            {isResultConfirmed ? (
              <Stack
                spacing={0.5}
                alignItems="flex-end"
                data-testid="ride-confirmed"
                data-composite={result.composite ?? undefined}
              >
                <Stack direction="row" spacing={0.5} alignItems="center" data-testid="ride-confirmed-badge">
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: "1.4rem" }} />
                  <Typography variant="h5" fontWeight={800} sx={{ color: "success.main" }}>
                    {hasComposite
                      ? t("results.confirmedBadge", {
                          composite: formatComposite(result.composite),
                        })
                      : t("results.confirmedBadgeNoScore")}
                  </Typography>
                </Stack>
                <Stack spacing={0.25} alignItems="flex-end">
                  {perJudge.map((j, i) => {
                    const hasJudgeScore = Number.isFinite(j.percentage);
                    return (
                      <Typography
                        key={judgeKey(j, i)}
                        variant="h6"
                        fontWeight={700}
                        data-testid="judge-score-chip"
                        data-position={j.position || undefined}
                        sx={{ color: "success.main" }}
                      >
                        {hasJudgeScore
                          ? t("results.judgeScore", {
                              position: judgeDisplay(j),
                              value: formatComposite(j.percentage),
                            })
                          : t("results.judgeScoreNoScore", {
                              position: judgeDisplay(j),
                            })}
                      </Typography>
                    );
                  })}
                </Stack>
              </Stack>
            ) : null}
            {/* #699 PARTIAL — some judges have committed their marks, others
                haven't. Show WHICH judges are committed, never a composite and
                never the plain `percentage` (score-api withholds both while
                PARTIAL). #3381: a confirmed judge's OWN number rides the chip,
                from the staff-only partialPercentage field. */}
            {isResultPartial ? (
              <Stack
                spacing={0.5}
                alignItems="flex-end"
                data-testid="ride-partial-committed"
                data-confirmed-count={confirmedCount}
                data-expected-count={expectedJudgeCount || undefined}
              >
                <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="flex-end">
                  {perJudge.map((j, i) => (
                    <Chip
                      key={judgeKey(j, i)}
                      size="small"
                      color="success"
                      icon={j.marksConfirmed ? <CheckCircleIcon /> : undefined}
                      data-testid="judge-committed-chip"
                      data-position={j.position || undefined}
                      data-confirmed={j.marksConfirmed ? "true" : "false"}
                      label={
                        // #3381 -- the confirmed judge's own number, from the
                        // staff-only partialPercentage field (never `percentage`,
                        // which score-api still withholds while PARTIAL).
                        j.marksConfirmed && Number.isFinite(j.partialPercentage)
                          ? t("results.judgeCommittedScore", {
                              position: judgeDisplay(j),
                              value: formatComposite(j.partialPercentage),
                            })
                          : t("results.judgeCommitted", { position: judgeDisplay(j) })
                      }
                      sx={{ fontWeight: 700 }}
                    />
                  ))}
                </Stack>
                {expectedJudgeCount > 0 ? (
                  <Typography variant="body2" color="text.secondary" data-testid="ride-committed-count">
                    {t("results.judgeCommittedCount", {
                      confirmed: confirmedCount,
                      expected: expectedJudgeCount,
                    })}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
            {roleStyle ? (
              <Chip
                label={roleLabel(row.role, row.status, t)}
                data-testid={roleStyle.testId}
                sx={{ ...roleStyle.sx, fontSize: "1rem", height: 34, px: 0.5 }}
              />
            ) : null}
            {/* #2366 — scribe presence, CURRENT ride only ("while a ride is
                up", AC1). One chip per seat GET /presence returned for
                this class (a seat nobody has been heard from in the last
                hour has no row; a seat that went quiet comes back tagged
                live:false, even after a page reload, #2370 — see
                utils/presence.js's file header). Elapsed time is always shown as TEXT (AC4), never
                colour alone; colour additionally flips to error/urgent past
                SCRIBE_QUIET_THRESHOLD_SECONDS (AC6). A seat/showId/ring/
                classId in a presence row is a CLAIM the scribe's own
                browser made, not an authorization fact (#2363's contract) —
                worded as "reporting in", never "assigned" or "confirmed".
                Sized to match the neighbouring role chip above (fontSize
                1rem, height 34), not MUI's size="small" default, so it is
                legible at the same working distance (AC2). */}
            {isCurrent && presence && presence.length > 0 ? (
              <Stack
                direction="row"
                spacing={0.5}
                flexWrap="wrap"
                justifyContent="flex-end"
                data-testid="scribe-presence-row"
              >
                {presence.map((p) => (
                  <Chip
                    key={p.seat}
                    color={p.quiet ? "error" : "success"}
                    icon={p.quiet ? <ErrorOutlineIcon /> : <CheckCircleIcon />}
                    data-testid="scribe-presence-chip"
                    data-seat={p.seat}
                    data-quiet={p.quiet ? "true" : "false"}
                    label={
                      p.quiet
                        ? t("presence.quiet", {
                            position: p.seat,
                            elapsed: formatElapsedSeconds(p.elapsedSeconds),
                          })
                        : t("presence.reporting", {
                            position: p.seat,
                            elapsed: formatElapsedSeconds(p.elapsedSeconds),
                          })
                    }
                    sx={{ fontWeight: 700, fontSize: "1rem", height: 34, px: 0.5 }}
                  />
                ))}
              </Stack>
            ) : null}
            {/* #2153: the CURRENT chip reads "Entering ring" for a belled ride
                (roleLabel above), so ENTERING_RING joins IN_PROGRESS in the
                dedupe below: one chip, never "In the ring" + "Entering ring".
                #758 unified the WORDING ("In the ring" both places) but left TWO
                chips saying it — the role chip above plus this status badge —
                stacked on the current ride (bug #80). #925: when the role chip
                ALREADY says "In the ring" (isCurrent + IN_PROGRESS), drop this
                one entirely rather than render the same phrase twice; every
                other status (Scheduled/Confirmed/Scored/Final/Scratched) still
                gets its own badge exactly as before.
                #2131 (docs/score/ring-truth.md) — "In the ring" is a ring-
                OCCUPANCY claim, and ring truth (the role chip's own source,
                resolveRingTruth) is the ONLY thing allowed to make that claim
                now. row.status === IN_PROGRESS means "this ride has an open,
                unconfirmed scoresheet" — real, but a DIFFERENT question, and
                a ride can read IN_PROGRESS long after the ring has genuinely
                moved on to another one (the #2083/#2131 "9am ghost" bug this
                whole card exists to fix). So the two cases below use two
                different labels: isCurrent already said "In the ring" via the
                role chip; a non-current IN_PROGRESS ride gets the status-only
                "Scoring" wording, never the ring-occupancy phrase. */}
            {chipReason ? (
              <TermReasonChip
                data-testid="ride-term-chip"
                termReason={chipReason}
                termLabels={termChipLabels(t)}
                sx={{ fontWeight: 700, fontSize: "1rem", height: 34, px: 0.5 }}
              />
            ) : isCurrent &&
              (row.status === RIDE_STATUSES.IN_PROGRESS ||
                row.status === RIDE_STATUSES.ENTERING_RING) ? null : (
              <RideStatusBadge
                status={row.status}
                labels={{
                  [RIDE_STATUSES.SCRATCHED]: t("oog.scratched"),
                  [RIDE_STATUSES.IN_PROGRESS]: t("oog.scoring"),
                  // #2153 -- a belled ride that is NOT the ring's current ride
                  // (ring truth points elsewhere) keeps its status badge, now
                  // translated rather than common-fe's hardcoded English.
                  [RIDE_STATUSES.ENTERING_RING]: t("oog.enteringRing"),
                }}
                sx={{ fontWeight: 700 }}
              />
            )}
            {testName ? (
              <Typography variant="body2" color="text.secondary">
                {testName}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails data-testid="ride-row-detail" sx={{ px: 2, pb: 2, pt: 0 }}>
        {hasAnyDetail ? (
          <Stack spacing={1.75} sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1.75 }}>
            {hasClassDetail ? (
              <Box data-testid="detail-class">
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block", lineHeight: 1.6, letterSpacing: 1 }}
                >
                  {t("detail.class")}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" fontWeight={500} sx={{ color: "text.primary" }}>
                    {classLine || "—"}
                  </Typography>
                  {row.qualifying ? (
                    <Chip
                      size="small"
                      label={t("detail.qualifying")}
                      data-testid="detail-qualifying"
                      sx={{ bgcolor: colors.foxOrange, color: "#000", fontWeight: 700 }}
                    />
                  ) : null}
                  {row.termReason ? (
                    <Chip size="small" variant="outlined" label={row.termReason} />
                  ) : null}
                </Stack>
              </Box>
            ) : null}

            <DetailLine testId="detail-horse" label={t("detail.horse")} value={row.horseDetail} />
            <DetailLine testId="detail-pedigree" label={t("detail.pedigree")} value={row.pedigree} />
            <DetailLine
              testId="detail-connections"
              label={t("detail.connections")}
              value={row.ownerLine}
            />
            <DetailLine testId="detail-rider-from" label={t("detail.riderFrom")} value={row.address} />
          </Stack>
        ) : (
          <Typography
            data-testid="detail-empty"
            color="text.secondary"
            sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1.75 }}
          >
            {t("detail.empty")}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
});

export default RideRow;
